import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { recoveryApi, ApiError } from '../services/recoveryApi';
import { getDownloadUrl, BarnApiError } from '../services/barnApi';
import { type CreateLegalCaseDocumentPayload, type LegalCaseDocument } from '../types/LegalCase';

// Gerencia os anexos de um caso: lista, vincula (o upload em si é do FileManager → barn) e
// remove. Baixar/visualizar delega ao barn (getDownloadUrl). Mesmo padrão do
// useLegalCaseDeposits — seededCaseRef re-seed por caso e guarda de corrida por geração —, sem
// update (anexo é imutável).
export default function useLegalCaseDocuments(
    caseId: string | null,
    initialDocuments?: LegalCaseDocument[],
) {
    const [documents, setDocuments] = useState<LegalCaseDocument[]>(initialDocuments || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const seededCaseRef = useRef<string | null>(null);
    const fetchGenRef = useRef(0);

    const fetchDocuments = useCallback(async () => {
        if (!caseId) return;
        const generation = ++fetchGenRef.current;
        setIsLoading(true);
        try {
            const result = await recoveryApi.getDocuments(caseId);
            if (fetchGenRef.current !== generation) return;
            setDocuments(result.items || []);
        } catch {
            // silencioso — a seção já mostra os anexos que vieram no page-view
        } finally {
            if (fetchGenRef.current === generation) setIsLoading(false);
        }
    }, [caseId]);

    useEffect(() => {
        if (seededCaseRef.current === caseId) return;
        seededCaseRef.current = caseId;
        setDocuments(initialDocuments || []);
        // Semeia com o page-view e re-busca (cobre a remontagem da seção ao alternar de aba —
        // mesmo motivo do useLegalCaseDeposits).
        fetchDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId]);

    // Vincula ao caso um arquivo já enviado ao barn (o FileManager fez o upload e devolveu o
    // fileId). Chamado no onUploadComplete do FileManager.
    const addDocument = async (data: CreateLegalCaseDocumentPayload): Promise<boolean> => {
        if (!caseId) return false;
        setIsSaving(true);
        try {
            await recoveryApi.addDocument(caseId, data);
            await fetchDocuments();
            toast.success('Anexo adicionado');
            return true;
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Dados inválidos.');
                return false;
            }
            toast.error(e?.data?.message || e?.message || 'Erro ao adicionar anexo');
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const removeDocument = async (documentId: string) => {
        if (!caseId) return;
        setIsSaving(true);
        try {
            await recoveryApi.removeDocument(caseId, documentId);
            await fetchDocuments();
            toast.success('Anexo removido');
        } catch (e: any) {
            toast.error(e?.data?.message || e?.message || 'Erro ao remover anexo');
        } finally {
            setIsSaving(false);
        }
    };

    return {
        documents, isLoading, isSaving,
        addDocument, removeDocument, fetchDocuments,
    };
}

// Abre o anexo numa nova aba (pré-visualizar) ou força o download, pedindo a URL assinada ao
// barn. Fora do hook (não depende de estado). `attachment` baixa com o nome original; `inline`
// abre no browser.
//
// A janela é aberta **de forma síncrona** no clique (antes do await), dentro da ativação
// transitória do usuário — senão o popup blocker bloquearia a aba, já que o window.open cairia
// depois do await da URL (apontamento do Copilot). Quando a URL resolve, redireciona a aba já
// aberta; se o popup foi bloqueado (win == null), cai para o mesmo tab.
export async function openDocument(
    barnFileId: string,
    disposition: 'inline' | 'attachment' = 'inline',
): Promise<void> {
    const win = window.open('', '_blank', 'noopener,noreferrer');
    try {
        const { downloadUrl } = await getDownloadUrl(barnFileId, disposition);
        if (win) {
            win.location.href = downloadUrl;
        } else {
            // Popup bloqueado: fallback para navegação na própria aba.
            window.location.href = downloadUrl;
        }
    } catch (e) {
        if (win) win.close();
        const message = e instanceof BarnApiError ? e.message : 'Erro ao abrir o arquivo';
        toast.error(message);
    }
}
